import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import Avatar from "../components/Avatar";
import FileUpload from "../components/FileUpload";
import { Save, Bell, BellOff, Loader2, Send, Key, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  permissionState,
  currentSubscription,
  enablePush,
  disablePush,
  sendTestPush,
} from "../lib/push";

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user) {
      setF({
        name: user.name || "",
        sector: user.sector || "",
        sub_specialties: (user.sub_specialties || []).join(", "),
        typical_client: user.typical_client || "",
        what_they_bring: user.what_they_bring || "",
        what_they_seek: user.what_they_seek || "",
        whatsapp: user.whatsapp || "",
        photo_url: user.photo_url || "",
      });
    }
  }, [user]);

  if (!f) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    await api.patch(`/members/${user.id}`, {
      ...f,
      sub_specialties: f.sub_specialties.split(",").map(s => s.trim()).filter(Boolean),
    });
    await refreshMe();
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const onPhotoUploaded = async () => {
    // Backend already updated user.photo_url. Refresh local user.
    await refreshMe();
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="profile-page">
      <div>
        <div className="overline">Me</div>
        <h1 className="font-serif text-4xl text-[#F0EDE8] mt-2 tracking-tight">My Profile</h1>
        <p className="text-[#9A9590] mt-3">Keep your profile sharp — it's how the table finds the right collaborator.</p>
      </div>
      <div className="ts-card p-7 flex items-center gap-5">
        <Avatar name={user.name} photoUrl={user.photo_url} color={user.avatar_color} size={64} />
        <div>
          <div className="font-serif text-xl text-[#F0EDE8]">{user.name}</div>
          <div className="overline mt-1">{user.group_role}</div>
        </div>
      </div>

      <div className="ts-card p-7" data-testid="profile-photo-section">
        <div className="overline mb-4">Profile photo</div>
        <FileUpload
          endpoint="/uploads/profile-photo"
          accept="image/jpeg,image/png,image/webp"
          maxBytes={5 * 1024 * 1024}
          previewUrl={user.photo_url}
          variant="avatar"
          onUploaded={onPhotoUploaded}
          dataTestid="profile-photo-upload"
        />
      </div>

      <NotificationsCard />

      <ChangePasswordCard />

      <form onSubmit={save} className="ts-card p-8 space-y-6" data-testid="profile-form">
        <div><label className="overline">Name</label><input className="ts-input" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})}/></div>
        <div><label className="overline">Sector</label><input className="ts-input" value={f.sector} onChange={(e)=>setF({...f,sector:e.target.value})}/></div>
        <div><label className="overline">Sub-specialties (comma separated)</label><input className="ts-input" value={f.sub_specialties} onChange={(e)=>setF({...f,sub_specialties:e.target.value})}/></div>
        <div><label className="overline">Typical client</label><input className="ts-input" value={f.typical_client} onChange={(e)=>setF({...f,typical_client:e.target.value})}/></div>
        <div><label className="overline">What you bring</label><textarea className="ts-input min-h-[80px] py-2" value={f.what_they_bring} onChange={(e)=>setF({...f,what_they_bring:e.target.value})}/></div>
        <div><label className="overline">What you're looking for</label><textarea className="ts-input min-h-[80px] py-2" value={f.what_they_seek} onChange={(e)=>setF({...f,what_they_seek:e.target.value})}/></div>
        <div><label className="overline">WhatsApp</label><input className="ts-input" value={f.whatsapp} onChange={(e)=>setF({...f,whatsapp:e.target.value})}/></div>
        <button type="submit" className="btn-gold rounded-md px-5 py-2.5 inline-flex items-center gap-2 text-sm" data-testid="save-profile">
          <Save size={14}/> {saving ? "Saving…" : saved ? "Saved" : "Save profile"}
        </button>
      </form>
    </div>
  );
}


function NotificationsCard() {
  const supported = isPushSupported();
  const [perm, setPerm] = useState(supported ? permissionState() : "unsupported");
  const [hasSubscription, setHasSubscription] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    setPerm(permissionState());
    const sub = await currentSubscription();
    setHasSubscription(!!sub);
    try {
      const { data } = await api.get("/push/status");
      setActiveCount(data.active_count || 0);
    } catch {
      setActiveCount(0);
    }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const onEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Notifications enabled.");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't enable notifications.");
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Notifications disabled on this device.");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      await sendTestPush();
      toast.success("Test push sent — check your lock screen.");
    } catch {
      toast.error("Couldn't send test push.");
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <div className="ts-card p-7" data-testid="notifications-card">
        <div className="overline mb-3">Notifications</div>
        <p className="text-sm text-[#9A9590] leading-relaxed">
          This browser doesn't support push notifications. Try Chrome on Android or Safari on iOS 16.4+.
        </p>
      </div>
    );
  }

  const enabled = perm === "granted" && hasSubscription;

  return (
    <div className="ts-card p-7" data-testid="notifications-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">Notifications</div>
          <h3 className="font-serif text-xl text-[#F0EDE8] mt-1.5">Lock-screen pings</h3>
          <p className="text-sm text-[#9A9590] mt-2 leading-relaxed max-w-md">
            New opportunities, deal comments, action items — pushed to this device when something
            needs your attention. We respect your focus and only ping for the things you'd actually
            want to know.
          </p>
        </div>
        <div className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center border ${
          enabled ? "bg-[#C9A84C]/15 border-[#C9A84C]/40" : "bg-[#1A1A1A] border-[#C9A84C]/20"
        }`}>
          {enabled
            ? <Bell size={20} className="text-[#C9A84C]" />
            : <BellOff size={20} className="text-[#9A9590]" />}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3 items-center">
        {enabled ? (
          <>
            <button
              onClick={onDisable}
              disabled={busy}
              className="btn-ghost-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="push-disable"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <BellOff size={14} />}
              Turn off on this device
            </button>
            <button
              onClick={onTest}
              disabled={testing}
              className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] hover:text-[#E1C97A] transition inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="push-test"
            >
              {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Send test
            </button>
          </>
        ) : perm === "denied" ? (
          <p className="text-xs text-red-400/80">
            You blocked notifications. Re-enable them in your browser settings (site permissions) and refresh.
          </p>
        ) : (
          <button
            onClick={onEnable}
            disabled={busy}
            className="btn-gold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="push-enable"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
            {busy ? "Enabling…" : "Enable notifications"}
          </button>
        )}
        {enabled && (
          <span className="text-[10px] uppercase tracking-widest text-[#9A9590]/70 ml-auto">
            {activeCount} device{activeCount === 1 ? "" : "s"} active
          </span>
        )}
      </div>
    </div>
  );
}


function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const strength = (() => {
    if (!next) return null;
    if (next.length < 10) return { label: "Too short (10+ characters)", color: "text-red-400/90" };
    const hasDigit = /\d/.test(next);
    const hasSymbol = /[^a-zA-Z0-9]/.test(next);
    const longEnough = next.length >= 14;
    if (longEnough && hasDigit && hasSymbol) return { label: "Strong", color: "text-[#C9A84C]" };
    if (hasDigit || hasSymbol) return { label: "Good", color: "text-[#E1C97A]" };
    return { label: "Weak — add a digit or symbol", color: "text-amber-400" };
  })();

  const mismatch = !!confirm && confirm !== next;
  const canSubmit = current && next.length >= 10 && next === confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed. Other devices will need to log in again.");
      setCurrent(""); setNext(""); setConfirm("");
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(" ") : detail || "Couldn't change password.";
      toast.error(typeof msg === "string" ? msg : "Couldn't change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="ts-card p-7 space-y-5" data-testid="change-password-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">Security</div>
          <h3 className="font-serif text-xl text-[#F0EDE8] mt-1.5">Change password</h3>
          <p className="text-sm text-[#9A9590] mt-2 leading-relaxed max-w-md">
            10+ characters. A mix of letters, numbers and symbols ages well — but the only
            real rule is: don't reuse it anywhere else.
          </p>
        </div>
        <div className="shrink-0 w-12 h-12 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/30 flex items-center justify-center">
          <Key size={20} className="text-[#C9A84C]" />
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="overline">Current password</label>
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              className="ts-input pr-10"
              value={current}
              autoComplete="current-password"
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="pw-current"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9A9590] hover:text-[#C9A84C]"
              aria-label={show ? "Hide" : "Show"}
              tabIndex={-1}
            >
              {show ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
        </div>
        <div>
          <label className="overline">New password</label>
          <input
            type={show ? "text" : "password"}
            className="ts-input"
            value={next}
            autoComplete="new-password"
            onChange={(e) => setNext(e.target.value)}
            data-testid="pw-new"
          />
          {strength && (
            <div className={`text-xs mt-1.5 ${strength.color}`} data-testid="pw-strength">
              {strength.label}
            </div>
          )}
        </div>
        <div>
          <label className="overline">Confirm new password</label>
          <input
            type={show ? "text" : "password"}
            className="ts-input"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="pw-confirm"
          />
          {mismatch && (
            <div className="text-xs text-red-400/90 mt-1.5">Passwords don't match.</div>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        className="btn-gold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="pw-submit"
      >
        {busy ? <Loader2 size={14} className="animate-spin"/> : done ? <Check size={14}/> : <Key size={14}/>}
        {busy ? "Updating…" : done ? "Updated" : "Update password"}
      </button>
    </form>
  );
}
